import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DialogComponent, DialogConfig } from '@shared/components/dialog';
import { ImageCropperDialogComponent, ImageCropperDialogData, ImageCropperDialogResult } from '@shared/components/image-cropper';
import { LanguageSwitcherDialogComponent } from '@shared/components/language-switcher-dialog/language-switcher-dialog.component';
import { SelectDialogConfig, SelectDialogResult } from '@shared/components/select-dialog/select-dialog-config';
import { SelectDialogComponent } from '@shared/components/select-dialog/select-dialog.component';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly matDialog = inject(MatDialog);

  /** Open a dialog. */
  public open<ResultType = any, DialogType = unknown, DataType = unknown>(
    component: ComponentType<DialogType>,
    config?: MatDialogConfig<DataType>,
  ) {
    return this.matDialog.open<DialogType, DataType, ResultType>(component, config);
  }

  /** Open a dialog, and return whatever the dialog returns after it closes. */
  public resolve<ResultType = any, DialogType = unknown, DataType = unknown>(component: ComponentType<DialogType>, config?: MatDialogConfig<DataType>) {
    const ref = this.open<ResultType>(component, config);
    return firstValueFrom(ref.afterClosed())
  }

  public openConfirmDialog(config: { data: DialogConfig; canClose?: boolean }): MatDialogRef<DialogComponent, boolean> {
    let { data, canClose } = config;
    canClose = canClose ?? true;

    return this.open(DialogComponent, {
      autoFocus: true,
      data,
      closeOnNavigation: canClose,
      disableClose: !canClose,
    });
  }

  public openSelectDialog<T>(config: { data: SelectDialogConfig<T> }): MatDialogRef<SelectDialogComponent, SelectDialogResult<T>> {
    return this.open(SelectDialogComponent, {
      closeOnNavigation: true,
      disableClose: false,
      autoFocus: true,
      data: config.data,
    });
  }

  public openImageCropper(data: ImageCropperDialogData): MatDialogRef<ImageCropperDialogComponent, ImageCropperDialogResult> {
    return this.open(ImageCropperDialogComponent, {
      closeOnNavigation: true,
      disableClose: false,
      data,
      panelClass: ['app-image-cropper-dialog-panel', 'zero-padding-dialog'],
    });
  }

  public openLanguageSwitcher(): MatDialogRef<LanguageSwitcherDialogComponent, undefined> {
    return this.open(LanguageSwitcherDialogComponent, {
      disableClose: false,
      panelClass: 'half-padding-dialog',
    });
  }
}

export function waitForDialogResult<T = any, R = any>(dialog: MatDialogRef<T, R>): Promise<R> {
  return new Promise((resolve) => {
    dialog.afterClosed().subscribe((result) => {
      resolve(result);
    });
  });
}
